import { Link } from '@tanstack/react-router'
import { ErrorNote, Loading } from '@/components/Feedback'
import { useDashboard } from '@/lib/queries'

function formatDate(iso: string | null): string {
  if (iso === null) return '—'
  return new Date(iso).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  })
}

/**
 * Counts and health at a glance — total items, locations, items needing
 * attention, and recent activity (spec §3.2 Dashboard).
 */
export function Dashboard() {
  const { data, isPending, error } = useDashboard()

  if (isPending) return <Loading what="Loading the dashboard" />
  if (error !== null) return <ErrorNote error={error} />

  const { stats, attention, lastStocktake, openStocktake } = data

  return (
    <>
      <h1 className="page-title">Dashboard</h1>
      <p className="page-lede">
        An overview of the tech department&rsquo;s kit: lighting, audio, electronics and cable
        stock.
      </p>

      <div className="stat-grid">
        <div className="card">
          <div className="card-kicker">Inventory records</div>
          <div className="card-title" style={{ fontSize: 28 }}>
            {stats.totalItems}
          </div>
          <p className="card-body">{stats.totalUnits} total units across all categories.</p>
        </div>

        <div className="card">
          <div className="card-kicker">PAT testing</div>
          <div className="card-title" style={{ fontSize: 28 }}>
            {stats.patDue}
          </div>
          <p className="card-body">Items overdue or missing a PAT record.</p>
        </div>

        <div className="card">
          <div className="card-kicker">Faults</div>
          <div className="card-title" style={{ fontSize: 28 }}>
            {stats.notWorking}
          </div>
          <p className="card-body">
            Items currently marked not working.
            {stats.notTested > 0 && ` ${stats.notTested} more have not been checked.`}
          </p>
        </div>

        <div className="card">
          <div className="card-kicker">Last stocktake</div>
          <div className="card-title" style={{ fontSize: 20 }}>
            {formatDate(lastStocktake?.completedAt ?? null)}
          </div>
          <p className="card-body">
            {lastStocktake === null
              ? 'No stocktake recorded yet.'
              : `${lastStocktake.countedLines}/${lastStocktake.totalLines} counted, ${lastStocktake.discrepancies} discrepancy${lastStocktake.discrepancies === 1 ? '' : 'ies'}.`}
          </p>
        </div>
      </div>

      {openStocktake !== null && (
        <div className="card elev-sm" style={{ maxWidth: 520, marginBottom: 'var(--space-6)' }}>
          <div className="card-kicker">In progress</div>
          <div className="card-title">A stocktake is underway</div>
          <p className="card-body">
            {openStocktake.countedLines} of {openStocktake.totalLines} items counted.
          </p>
          <Link to="/stocktake" className="btn btn-primary" style={{ alignSelf: 'flex-start' }}>
            Carry on counting
          </Link>
        </div>
      )}

      <div className="action-row" style={{ margin: 'var(--space-6) 0 var(--space-8)' }}>
        <Link to="/inventory/new" className="btn btn-primary">
          + Add equipment
        </Link>
        <Link to="/stocktake" className="btn btn-secondary">
          Start a stocktake
        </Link>
        <Link to="/inventory" className="btn btn-ghost">
          Browse inventory
        </Link>
      </div>

      <div className="section-title">Needs attention</div>

      {attention.length === 0 ? (
        <p className="empty-note">Nothing needs attention right now.</p>
      ) : (
        <div className="table-scroll">
          <table className="table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Category</th>
                <th>Location</th>
                <th>Issue</th>
              </tr>
            </thead>
            <tbody>
              {attention.map((item) => (
                <tr key={item.id}>
                  <td>
                    <Link
                      to="/inventory/$itemId"
                      params={{ itemId: String(item.id) }}
                      className="row-link"
                    >
                      {item.displayName}
                    </Link>
                  </td>
                  <td>{item.category}</td>
                  <td className="text-muted">{item.location?.name ?? '—'}</td>
                  <td>
                    <span className="tag tag-accent-2">{item.issue}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  )
}
